import { Controller } from '@hotwired/stimulus';
import { CR_INLINE_FIELD_EVENTS } from './cr_inline_field_protocol';

// Drag values whose trailing write is still OWED, keyed by the NUMBER input's `id`.
//
// MODULE-scoped on purpose (same idiom as `cr_inline_field_protocol`): a settled inline
// save — 2xx AND 422 — replaces the whole row it lives in
// (`meeting_reports/lot_progress/_write_streams.turbo_stream.erb`), so the controller
// instance that deferred a drag is already disconnected when the save settles. The
// REPLACEMENT instance is the only object left that can emit the trailing write, and it
// finds the owed value here. An entry is removed the moment it is consumed, superseded
// by a live commit, or discarded for a Turbo page cache — so at most one short string
// per field is ever held, and it never outlives the page it was dragged on.
//
// A number input with no `id` (no stable identity across a replacement) falls back to
// per-instance storage: it still gets the trailing write on every path that does NOT
// replace its node.
const deferredCommits = new Map();

/**
 * range-input — keeps a range slider and a number input two-way in sync, and
 * (optionally) forwards a slider RELEASE to the number input's own `change`
 * handler so a downstream transport (e.g. `cr-inline-field`) saves exactly once
 * per drag gesture.
 *
 * Generic, dependency-free: min/max/step are read from the DOM attributes of
 * the number input (falling back to the slider's attributes). Typing an
 * out-of-range value into the number field is clamped, and the corrected value
 * is written back into both inputs.
 *
 * Targets: slider (<input type="range">), number (<input type="number">)
 *
 * Actions:
 *   sync             (input->range-input#sync on BOTH inputs) — mirrors the edited
 *                    input into the other; clamps when the number is the source.
 *   captureCommitted (pointerdown->range-input#captureCommitted on the slider) —
 *                    snapshots the number value at drag START as the baseline the
 *                    release compares against.
 *   commit           (change->range-input#commit on the slider) — mirrors the
 *                    slider into the number, then re-dispatches a bubbling `change`
 *                    on the number ONLY when the value moved since the committed
 *                    baseline, so a downstream save fires once and an unchanged
 *                    drag (input->sync already equalized the two) is a no-op.
 *
 * Committed-value semantics: `committedValue` is the value the number input is
 * known to have last saved / been rendered with. It is seeded on connect from the
 * server value (so a keyboard-only arrow sequence ending in `change` compares
 * against reality), re-snapshotted at each drag start (captureCommitted), advanced
 * after every commit that dispatches, and re-converged when the shared cr-inline-field
 * protocol SETTLES a save (succeeded OR failed) for THIS controller's number input.
 *
 * captureCommitted ALSO seeds any downstream committed-snapshot capture (the
 * cr-inline-field controller ignores the nameless slider at pointer capture) with the
 * PRE-DRAG value, so a failed slider save restores the pre-drag value — not the
 * unsaved dragged one — keeping this baseline and the server in agreement.
 *
 * Connect-time mirroring: the slider is aligned to the number's value ONLY when that
 * value is one the range can hold VERBATIM. A 422 row re-render deliberately renders
 * the two inputs APART (number = the raw rejected attempt, range = the last-known-good
 * persisted value) and this controller must not undo that — see
 * `#mirrorNumberOntoSlider`.
 *
 * Deferred-commit semantics (an inline save in flight): `cr-inline-field` disables the
 * field's named controls while a PATCH runs, but the slider is deliberately NAMELESS so
 * it stays draggable. A release during that window neither dispatches immediately (that
 * would queue one extra PATCH per drag) nor is dropped (a dropped drag is invisible to
 * `cr-diffusion-guard#writesInFlight`, so « Figer et diffuser » could freeze the CR with
 * the drag never persisted). The latest value is remembered instead, and ONE trailing
 * `change` is emitted when the field settles: on the protocol's terminal event for this
 * number input, or — when the settling save replaced the row — from the replacement
 * instance's own connect.
 *
 * @example
 * <div data-controller="range-input">
 *   <input type="range" min="0" max="100" step="1"
 *          data-range-input-target="slider"
 *          data-action="input->range-input#sync
 *                       pointerdown->range-input#captureCommitted
 *                       change->range-input#commit">
 *   <input type="number" min="0" max="100" step="1"
 *          data-range-input-target="number"
 *          data-action="input->range-input#sync change->cr-inline-field#patchField">
 * </div>
 */
export default class extends Controller {
  static targets = ['slider', 'number'];

  connect() {
    this.isControllerConnected = true;
    this.deferredValue = null;

    // Align the slider to the number's server-provided value on first render — but only
    // when the number holds a value the range can represent exactly (see the method).
    this.#mirrorNumberOntoSlider();

    // Seed the committed baseline from the server value so a keyboard-only arrow
    // sequence that never emits a pointerdown still compares against reality.
    this.committedValue = this.hasNumberTarget ? this.numberTarget.value : null;

    // Re-converge the baseline (and flush any owed trailing write) whenever the shared
    // inline-save protocol SETTLES a save for THIS number input — succeeded (value = the
    // just-saved value) OR failed (value = the snapshot the protocol just restored).
    // Covers the paths that do not replace the row (empty-stream success, network/
    // non-stream failure); a row-replace instead reconnects this controller, which
    // re-seeds in connect() and flushes from the module-scoped hand-off below.
    this.saveSettledHandler = this.#handleSaveSettled.bind(this);
    this.discardDeferredHandler = this.#discardDeferredCommit.bind(this);
    window.addEventListener(CR_INLINE_FIELD_EVENTS.succeeded, this.saveSettledHandler);
    window.addEventListener(CR_INLINE_FIELD_EVENTS.failed, this.saveSettledHandler);
    // A Turbo navigation snapshots the page while this controller is still connected —
    // the one moment we can tell « navigating away » from « my row was replaced » — so a
    // drag owed a trailing write on THIS page never fires on a later restore.
    document.addEventListener('turbo:before-cache', this.discardDeferredHandler);

    // A trailing write owed by the PREVIOUS instance of this field (its row was replaced
    // by the save that is settling). Deferred by one microtask so the whole Stimulus
    // mutation batch — including the host `cr-inline-field` controller's action bindings
    // on this very number input — is connected before the `change` is dispatched.
    // Microtasks run ahead of any timer, so the guard's deferred continuation cannot slip
    // in between and see an empty in-flight set.
    queueMicrotask(() => this.#flushDeferredCommit());
  }

  disconnect() {
    this.isControllerConnected = false;
    window.removeEventListener(CR_INLINE_FIELD_EVENTS.succeeded, this.saveSettledHandler);
    window.removeEventListener(CR_INLINE_FIELD_EVENTS.failed, this.saveSettledHandler);
    document.removeEventListener('turbo:before-cache', this.discardDeferredHandler);
    // Instance state only: an owed trailing write keyed by the number's id lives in the
    // module map ON PURPOSE — the replacement instance is what emits it.
    this.deferredValue = null;
  }

  sync(event) {
    if (event.target === this.sliderTarget) {
      this.#syncFromSlider();
    } else if (event.target === this.numberTarget) {
      this.#syncFromNumber();
    }
  }

  /**
   * Slider pointerdown: snapshot the number value at drag start as the baseline
   * the release compares against (`input->sync` equalizes both inputs during the
   * drag, so the release can only detect a change against this pre-drag snapshot).
   */
  captureCommitted() {
    if (!this.hasNumberTarget) return;

    // While a trailing write is still owed, the number input holds that OWED (unsaved)
    // drag — adopting it as the baseline would make the trailing write look already
    // committed and drop it. Keep the last genuinely committed value instead.
    if (!this.#hasDeferredCommit()) this.committedValue = this.numberTarget.value;

    // Seed any downstream committed-snapshot capture (cr-inline-field ignores the
    // nameless slider) with the PRE-DRAG value while the number still holds it —
    // before `input->sync` copies the new value in. cr-inline-field captures on
    // `focusin`/`pointerdown`; re-announcing focusin on the number runs its GENERIC
    // capture path (no avancement-specific knowledge added there), so a FAILED save
    // restores the pre-drag value, not the unsaved dragged one. A synthetic focusin
    // does NOT move focus and is a no-op where no such capture listener exists
    // (e.g. the planning phase form).
    this.numberTarget.dispatchEvent(new Event('focusin', { bubbles: true }));
  }

  /**
   * Slider release (`change`): mirror the slider into the number, then re-dispatch
   * a bubbling `change` on the number ONLY when the value moved since the committed
   * baseline — so the number's own `change->cr-inline-field#patchField` runs a
   * single save, and a drag that ended on the original value is a no-op.
   *
   * While an inline save is in flight the number input is disabled by cr-inline-field
   * and the (nameless, still interactive) slider can be dragged again. Dispatching then
   * would queue one EXTRA PATCH per drag behind the shared per-report queue; returning
   * silently would be worse — the drag would leave no `cr-inline-field:pending` and no
   * Turbo submission, so `cr-diffusion-guard#writesInFlight()` could not see it and
   * « Figer et diffuser » could freeze the CR with that drag never persisted. So the
   * latest value is DEFERRED and emitted once on settle (#flushDeferredCommit).
   */
  commit() {
    if (!this.hasNumberTarget) return;

    this.#syncFromSlider();
    if (this.numberTarget.value === this.committedValue) {
      // A drag that lands back on the committed value also CANCELS any owed trailing
      // write: this release, not an earlier one, is the user's latest intent.
      this.#discardDeferredCommit();
      return;
    }

    if (this.numberTarget.disabled) {
      this.#deferCommit(this.numberTarget.value);
      return;
    }

    this.#dispatchCommit();
  }

  #syncFromSlider() {
    if (!this.hasNumberTarget) return;
    this.numberTarget.value = this.sliderTarget.value;
  }

  #syncFromNumber() {
    const clamped = this.#clamp(this.numberTarget.value);
    if (clamped === null) return;

    // Write back to the number target too so out-of-range typed values are corrected.
    if (this.hasNumberTarget) this.numberTarget.value = clamped;
    if (this.hasSliderTarget) this.sliderTarget.value = clamped;
  }

  /**
   * Emits the number input's `change` for the downstream transport and advances the
   * baseline. Any owed trailing write is dropped first: this live commit carries a
   * newer value and supersedes it.
   */
  #dispatchCommit() {
    this.#discardDeferredCommit();
    this.numberTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.committedValue = this.numberTarget.value;
  }

  /**
   * Aligns the slider to the number input — ONLY when the number currently holds a value
   * the range can hold VERBATIM.
   *
   * On a 422 row re-render the server renders the two inputs APART on purpose: the number
   * keeps the RAW rejected attempt ('150', '-5', '12.5') so the user can correct what they
   * typed, while the range keeps the LAST-KNOWN-GOOD PERSISTED value — a browser paints a
   * range whose value is empty/non-numeric at its MIDPOINT and pins '150' to a FULL bar,
   * and on an editable row that slider is the only visual bar. Copying the rejected attempt
   * back here, one frame after paint, would re-create exactly the misleading full/empty bar
   * the server render exists to remove. So anything the range cannot hold verbatim leaves
   * the SERVER-RENDERED slider value untouched.
   *
   * When both inputs carry the same server value (every non-422 render) this is the
   * unchanged, unconditional mirror.
   */
  #mirrorNumberOntoSlider() {
    if (!this.hasSliderTarget || !this.hasNumberTarget) return;
    if (!this.#representableBySlider(this.numberTarget.value)) return;

    this.sliderTarget.value = this.numberTarget.value;
  }

  /**
   * Whether a raw string is a value the range input can hold verbatim: a finite number
   * whose canonical form is the string itself (so '', ' 12', '1e2' are out — and a
   * `type=number` already sanitizes 'abc' to ''), inside any min/max, and sitting on the
   * step ladder ('12.5' is out at step="1", in at step="0.5"). Bounds and step come from
   * the DOM, so this stays generic.
   *
   * @param {string} rawValue
   * @returns {boolean}
   */
  #representableBySlider(rawValue) {
    const raw = String(rawValue ?? '');
    if (raw === '') return false;

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || String(parsed) !== raw) return false;

    const min = this.#bound('min');
    const max = this.#bound('max');
    if (min !== null && parsed < min) return false;
    if (max !== null && parsed > max) return false;

    return this.#matchesStep(parsed, min);
  }

  /**
   * Whether a value sits on the input's step ladder, measured from `min` (the HTML step
   * base) or 0. A missing or non-numeric step (`step="any"`) constrains nothing. The
   * epsilon absorbs binary-float error on decimal steps.
   *
   * @param {number} value
   * @param {number|null} min
   * @returns {boolean}
   */
  #matchesStep(value, min) {
    const step = this.#bound('step');
    if (step === null || step <= 0) return true;

    const steps = (value - (min ?? 0)) / step;
    return Math.abs(steps - Math.round(steps)) < 1e-9;
  }

  /**
   * Re-converges the committed baseline when a protocol save SETTLES for this number
   * input — succeeded (the number now holds the just-saved value) or failed (the
   * protocol has already restored the number to its pre-drag snapshot before emitting
   * the failed event) — then emits any trailing write a drag deferred while the field
   * was disabled. The protocol emits the ORIGINAL field element in `detail.field`; on
   * the row-replace path this controller has already reconnected (and re-seeded from
   * the server value), so the identity check won't match — a safe no-op there, because
   * the replacement instance already flushed the hand-off in connect().
   *
   * @param {CustomEvent} event
   */
  #handleSaveSettled(event) {
    if (!this.hasNumberTarget) return;
    if (event.detail?.field !== this.numberTarget) return;

    // Flush BEFORE re-converging: `commit()` already mirrored the deferred drag into the
    // (disabled) number input, so a baseline taken first would equal the owed value and
    // make the trailing write look unchanged — silently dropping the drag.
    this.#flushDeferredCommit();
    this.committedValue = this.numberTarget.value;
  }

  /**
   * Remembers the LATEST value a drag released while an inline save was in flight. A
   * further drag overwrites it, so N drags coalesce into ONE trailing write.
   *
   * @param {string} value
   */
  #deferCommit(value) {
    const key = this.#deferralKey();
    if (key) {
      deferredCommits.set(key, value);
    } else {
      this.deferredValue = value;
    }
  }

  /**
   * Applies an owed trailing value to both inputs and emits its single `change`.
   *
   * The dispatch is deliberate even when the number input is still disabled (another
   * save already in flight): a programmatically dispatched event DOES fire listeners on
   * a disabled element, cr-inline-field's shared per-report queue serializes the write,
   * and registering it is precisely what keeps the drag visible to
   * `cr-diffusion-guard#writesInFlight()`. `commit()`'s early-outs are preserved — an
   * absent number target and a value equal to the committed baseline both no-op.
   */
  #flushDeferredCommit() {
    if (!this.isControllerConnected || !this.hasNumberTarget) return;

    const value = this.#takeDeferredCommit();
    if (value === null) return;

    if (this.hasSliderTarget) {
      this.sliderTarget.value = value;
      this.#syncFromSlider();
    } else {
      this.numberTarget.value = value;
    }

    if (this.numberTarget.value === this.committedValue) return;

    this.#dispatchCommit();
  }

  /**
   * Whether a trailing write is currently owed for this field.
   *
   * @returns {boolean}
   */
  #hasDeferredCommit() {
    const key = this.#deferralKey();
    return key ? deferredCommits.has(key) : this.deferredValue !== null;
  }

  /**
   * Consumes the owed trailing value for this field, or null when none is owed.
   *
   * @returns {string|null}
   */
  #takeDeferredCommit() {
    const key = this.#deferralKey();
    if (!key) {
      const value = this.deferredValue;
      this.deferredValue = null;
      return value ?? null;
    }

    if (!deferredCommits.has(key)) return null;

    const value = deferredCommits.get(key);
    deferredCommits.delete(key);
    return value;
  }

  /**
   * Drops an owed trailing value — superseded by a live commit, or abandoned because the
   * page is being cached for a Turbo navigation. Also serves as the `turbo:before-cache`
   * handler, whose event argument is deliberately ignored.
   */
  #discardDeferredCommit() {
    const key = this.#deferralKey();
    if (key) deferredCommits.delete(key);
    this.deferredValue = null;
  }

  /**
   * The identity a trailing write is parked under: the number input's `id`, which is the
   * only thing that survives Turbo replacing the row (the CR avancement field mints a
   * per-phase unique id). Null means « no cross-replacement hand-off » — the value stays
   * on this instance.
   *
   * @returns {string|null}
   */
  #deferralKey() {
    if (!this.hasNumberTarget) return null;

    return this.numberTarget.id || null;
  }

  #clamp(rawValue) {
    const value = parseFloat(rawValue);
    if (Number.isNaN(value)) return null;

    const min = this.#bound('min');
    const max = this.#bound('max');

    let result = value;
    if (min !== null && result < min) result = min;
    if (max !== null && result > max) result = max;
    return result;
  }

  // Read a numeric bound from the number target, falling back to the slider.
  #bound(name) {
    const fromNumber = this.hasNumberTarget ? this.numberTarget.getAttribute(name) : null;
    const fromSlider = this.hasSliderTarget ? this.sliderTarget.getAttribute(name) : null;
    const raw = fromNumber ?? fromSlider;
    if (raw === null || raw === '') return null;

    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
